import Papa from 'papaparse';

// nflverse player_stats_season — pre-aggregated season totals, no year suffix = always latest available
// As of March 2026, latest is 2024 season (2025 not yet published by nflverse)
const CSV_URL = 'https://github.com/nflverse/nflverse-data/releases/download/player_stats/player_stats_season.csv';
const PLAYER_SEASON_JSON = '/data/player_season_2024.json';

// Module-level cache
let _cache   = null;
let _promise = null;
let _latestSeason = null;

export function getNflverseLatestSeason() { return _latestSeason; }

// ── Name normalization ────────────────────────────────────────────────────
export function normalizeName(name) {
  return (name || '')
    .toLowerCase()
    .replace(/\b(jr|sr|ii|iii|iv|v)\.?\b/g, '')
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function safeNum(v) {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function buildMap(rows) {
  // Find the latest REG season in the file
  let maxSeason = 0;
  rows.forEach(row => {
    if (row.season_type === 'REG') {
      const s = parseInt(row.season);
      if (s > maxSeason) maxSeason = s;
    }
  });
  _latestSeason = maxSeason;

  const map = {};
  rows.forEach(row => {
    if (row.season_type !== 'REG') return;
    if (parseInt(row.season) !== maxSeason) return;

    const displayName = row.player_display_name || row.player_name || '';
    const key = normalizeName(displayName);
    if (!key) return;

    const attempts    = safeNum(row.attempts);
    const carries     = safeNum(row.carries);
    const targets     = safeNum(row.targets);
    const receptions  = safeNum(row.receptions);
    const passingEpa  = safeNum(row.passing_epa);
    const rushingEpa  = safeNum(row.rushing_epa);
    const receivingEpa= safeNum(row.receiving_epa);
    const airYards    = safeNum(row.receiving_air_yards);
    const yac         = safeNum(row.receiving_yards_after_catch);
    const games       = safeNum(row.games);

    map[key] = {
      displayName,
      position:    row.position,
      team:        row.recent_team,
      season:      maxSeason,
      games,
      // raw totals
      passingEpa, attempts,
      rushingEpa,  carries,
      receivingEpa,targets, receptions,
      airYards, yac,
      fantasyPts:    safeNum(row.fantasy_points),
      fantasyPtsPpr: safeNum(row.fantasy_points_ppr),
      targetShare:   safeNum(row.target_share),
      wopr:          safeNum(row.wopr),
      dakota:        safeNum(row.dakota),  // composite QB metric
      racr:          safeNum(row.racr),    // receiver air conversion ratio
      // derived rates
      passingEpaPerAttempt:  attempts   > 0 ? parseFloat((passingEpa  / attempts).toFixed(3))   : null,
      rushingEpaPerCarry:    carries    > 0 ? parseFloat((rushingEpa  / carries).toFixed(3))    : null,
      receivingEpaPerTarget: targets    > 0 ? parseFloat((receivingEpa/ targets).toFixed(3))    : null,
      avgAirYards:           receptions > 0 ? parseFloat((airYards    / receptions).toFixed(1)) : null,
      avgYac:                receptions > 0 ? parseFloat((yac         / receptions).toFixed(1)) : null,
      fantasyPtsPg:          games      > 0 ? parseFloat((safeNum(row.fantasy_points)     / games).toFixed(1)) : null,
      fantasyPprPg:          games      > 0 ? parseFloat((safeNum(row.fantasy_points_ppr) / games).toFixed(1)) : null,
    };
  });

  return map;
}

// ── Public API ────────────────────────────────────────────────────────────
export async function fetchNflverseStats() {
  if (_cache)   return _cache;
  if (_promise) return _promise;

  _promise = new Promise((resolve, reject) => {
    Papa.parse(CSV_URL, {
      download:       true,
      header:         true,
      skipEmptyLines: true,
      complete: (results) => {
        const map  = buildMap(results.data);
        _cache     = map;
        _promise   = null;
        resolve(map);
      },
      error: (err) => {
        _promise = null;
        reject(err);
      },
    });
  });

  return _promise;
}

/**
 * Build ESPN player ID → nflverse stats lookup via normalized name matching.
 */
export function buildEspnNflverseLookup(nflverseMap, espnPlayers) {
  const lookup = {};
  Object.values(espnPlayers).forEach(player => {
    const key = normalizeName(player.name);
    if (nflverseMap[key]) lookup[player.id] = nflverseMap[key];
  });
  return lookup;
}

// ── Player Season Stats (pre-processed JSON, keyed by ESPN ID) ───────────
let _playerSeasonCache = null;

export async function fetchPlayerSeasonStats() {
  if (_playerSeasonCache) return _playerSeasonCache;
  const r = await fetch(PLAYER_SEASON_JSON);
  const data = await r.json();
  // Build lookup: espnId → stats object
  const lookup = {};
  for (const [key, p] of Object.entries(data.players || {})) {
    // Key is espn_id when available, else gsis_{id}
    lookup[key] = p;
  }
  _playerSeasonCache = { lookup, season: data.season };
  return _playerSeasonCache;
}

// ── Player Advanced Stats (PBP-derived) ──────────────────────────────────
// Keyed by abbreviated PBP name ("P.Mahomes") — joined to ESPN by first initial + last name

let _playerAdvCache = null;

export async function fetchPlayerAdvancedStats() {
  if (_playerAdvCache) return _playerAdvCache;
  const r = await fetch('/data/player_advanced_2024.json');
  const data = await r.json();
  _playerAdvCache = data;
  return data;
}

/** Convert ESPN full name to PBP abbreviated format: "Patrick Mahomes" → "P.Mahomes" */
export function abbrevName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  return `${parts[0][0]}.${parts.slice(1).join(' ')}`;
}

/**
 * Build ESPN player ID → player advanced stats lookup.
 * Uses abbreviated name matching (PBP format).
 */
export function buildPlayerAdvancedLookup(playerAdvData, espnPlayers) {
  if (!playerAdvData) return {};
  const { qb = {}, rb = {}, wr = {} } = playerAdvData;
  const allPbp = { ...qb, ...rb, ...wr };

  const lookup = {};
  Object.values(espnPlayers).forEach(player => {
    const abbrev = abbrevName(player.name);
    if (allPbp[abbrev]) lookup[player.id] = allPbp[abbrev];
  });
  return lookup;
}
